import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  forwardRef
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';

@Component({
  selector: 'app-bay-level-textbox',
  template: `
    <input
      type="text"
      [class]="classValue"
      [disabled]="disabled"
      (input)="handleInput()"
      (focus)="handleFocus()"
      (blur)="handleBlur()"
      (keydown)="handleKeyDown($event)"
      #levelInput
      [placeholder]="placeholder"
      [attr.maxlength]="maxlength"
    />
  `,
  styles: [
    `
      input {
        text-align: center;
      }
      .is-invalid {
        border-color: #dc3545;
      }
    `
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BayLevelTextboxComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BayLevelTextboxComponent),
      multi: true
    }
  ],
  exportAs: 'BayLevelTextboxComponent'
})
export class BayLevelTextboxComponent implements OnChanges, ControlValueAccessor, Validator {
  private readonly renderer2: Renderer2;
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private inputValue: string | null = null;
  private focused = false;

  @ViewChild('levelInput', { static: true }) levelInput!: ElementRef;
  @Input() cssClass = '';
  @Input() disabled = false;
  @Input() placeholder = '0';
  @Input() maxlength = '5';
  @Input() minValue = 1;
  @Input() maxValue = 99.99;

  @Output() valueChange: EventEmitter<string | null> = new EventEmitter();
  @Output() focus: EventEmitter<void> = new EventEmitter();
  @Output() blur: EventEmitter<string | null> = new EventEmitter();
  @Output() enter: EventEmitter<void> = new EventEmitter();
  @Output() escape: EventEmitter<void> = new EventEmitter();

  constructor(renderer2: Renderer2) {
    this.renderer2 = renderer2;
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
  }

  validate(control: AbstractControl): { [key: string]: any } | null {
    if (control.value === null || control.value === '') {
      return { required: true };
    }

    const valueStr = String(control.value);
    const valueNum = parseFloat(valueStr);

    if (isNaN(valueNum)) {
      return { invalidFormat: true };
    }

    if (valueNum === 0) {
      return { isZero: true };
    }

    const parts = valueStr.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];

    if (
      valueStr.length > 5 ||
      !/^\d+(\.\d{0,2})?$/.test(valueStr) ||
      (decimalPart && decimalPart.length > 2) ||
      valueNum > this.maxValue ||
      integerPart.length > 2
    ) {
      return { invalidFormat: true };
    }

    return null;
  }

  writeValue(value: string | null): void {
    this.inputValue = value;
    this.setInputValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.inputValue) {
      this.setInputValue(changes.inputValue.currentValue);
    }
  }

  handleInput(): void {
    const element = this.levelInput.nativeElement;
    let value = element.value;

    if (!/^\d+(\.\d{0,2})?$/.test(value) && value !== '') {
      element.value = this.inputValue ?? '';
    } else {
      const parts = value.split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1];

      if (decimalPart && decimalPart.length > 2) {
        value = value.substring(0, value.length - 1);
        element.value = value;
      }

      if (integerPart && integerPart.length > 2) {
        value = value.substring(0, value.length - 1);
        element.value = value;
      }

      const valueNum = parseFloat(value);
      if (valueNum > this.maxValue) {
        value = this.maxValue.toFixed(decimalPart ? decimalPart.length : 0);
        element.value = value;
      }

      this.updateValue(value);
    }
  }

  handleFocus(): void {
    if (!this.focused) {
      this.focused = true;
    }
    this.focus.emit();
  }

  handleBlur(): void {
    if (this.focused) {
      this.focused = false;
      this.onTouched();
    }
    this.blur.emit(this.inputValue);
  }

  handleKeyDown(event: KeyboardEvent): void {
    if (!this.disabled) {
      switch (event.key) {
        case 'Enter':
          this.enter.emit();
          break;
        case 'Esc': // IE/Edge specific value
        case 'Escape':
          this.escape.emit();
          break;
        default:
      }
    }
  }

  private setInputValue(value: string | null = this.inputValue): void {
    const inputValue = value !== null ? value : '';
    this.renderer2.setProperty(this.levelInput.nativeElement, 'value', inputValue);
    this.inputValue = value;
  }

  private updateValue(value: string | null): void {
    if (this.inputValue !== value) {
      this.inputValue = value;
      this.onChange(value);
      this.valueChange.emit(value);
    }
  }

  get classValue(): string {
    return `${this.cssClass} form-control w-100 td-numeric-textbox-center`;
  }
}
